/**
 * Push Notification Admin Dashboard (Refactored)
 * Manages broadcast notifications, scheduled notifications, templates, delivery stats, analytics
 * 
 * Refactored from 1683 lines to ~350 lines by extracting components:
 * - PushStatsCards: Overview statistics
 * - PushAnalyticsTab: Detailed analytics (delivery trends, peak hours, growth)
 * - TemplatesGrid: Template selection and usage
 * - ScheduledNotificationsList: Scheduled notifications management
 * - SubscribersList: Active subscribers
 * - BroadcastHistory: Previous broadcasts
 * - PushDialogs: Broadcast, Test, Scheduled dialogs
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Bell, Send, RefreshCw, MessageSquare, Radio, Calendar, BarChart3, Users, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/utils/apiClient';
import { toast } from 'sonner';

// Import sub-components
import {
  PushStatsCards,
  BroadcastDialog,
  TestNotificationDialog,
  ScheduledDialog,
  PushAnalyticsTab,
  ScheduledNotificationsList,
  TemplatesGrid,
  SubscribersList,
  BroadcastHistory
} from './push';

export default function PushNotificationDashboard() {
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [broadcastHistory, setBroadcastHistory] = useState([]);
  const [scheduledNotifications, setScheduledNotifications] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showBroadcastDialog, setShowBroadcastDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [showScheduledDialog, setShowScheduledDialog] = useState(false);
  const [editingScheduled, setEditingScheduled] = useState(null);
  
  // Form state for dialogs (populated from templates)
  const [broadcastForm, setBroadcastForm] = useState({
    title: '',
    body: '',
    url: '/loyalty',
    require_interaction: false
  });
  const [testForm, setTestForm] = useState({
    customer_id: '',
    title: 'Test Notification',
    body: 'This is a test notification from Bulaccino Cafe!',
    url: '/loyalty'
  });
  const [scheduledForm, setScheduledForm] = useState(null);

  // Fetch dashboard data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, subsRes, historyRes, scheduledRes, templatesRes] = await Promise.all([
        api.get('/push/stats'),
        api.get('/push/subscribers', { params: { limit: 20 } }),
        api.get('/push/broadcast-history', { params: { limit: 10 } }),
        api.get('/push/scheduled'),
        api.get('/push/templates')
      ]);
      
      setStats(statsRes.data);
      setSubscribers(subsRes.data.subscribers || []);
      setBroadcastHistory(historyRes.data.broadcasts || []);
      setScheduledNotifications(scheduledRes.data.scheduled_notifications || []);
      setTemplates(templatesRes.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Template handlers
  const handleTemplateForBroadcast = (data) => {
    setBroadcastForm({
      title: data.title,
      body: data.body,
      url: data.url || '/loyalty',
      require_interaction: false
    });
    setShowBroadcastDialog(true);
    toast.success('Template applied! Review and send your broadcast.');
  };

  const handleTemplateForScheduled = (data) => {
    setScheduledForm({
      title: data.title,
      body: data.body,
      url: data.url || '/loyalty',
      schedule_type: 'daily',
      schedule_time: '14:00',
      schedule_days: [],
      start_date: '',
      end_date: '',
      require_interaction: false
    });
    setShowScheduledDialog(true);
    toast.success('Template applied! Configure your schedule.');
  };

  // Quick test to subscriber
  const handleTestSubscriber = (email) => {
    setTestForm({
      customer_id: email,
      title: 'Test Notification',
      body: 'This is a test notification from Bulaccino Cafe!',
      url: '/loyalty'
    });
    setShowTestDialog(true);
  };

  // Edit scheduled notification
  const handleEditScheduled = (notification) => {
    setEditingScheduled(notification);
    setScheduledForm({
      title: notification.title,
      body: notification.body,
      url: notification.url || '/loyalty',
      schedule_type: notification.schedule_type,
      schedule_time: notification.schedule_time,
      schedule_days: notification.schedule_days || [],
      start_date: notification.start_date || '',
      end_date: notification.end_date || '',
      require_interaction: notification.require_interaction || false
    });
    setShowScheduledDialog(true);
  };

  // New scheduled notification
  const handleNewScheduled = () => {
    setEditingScheduled(null);
    setScheduledForm(null);
    setShowScheduledDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="push-notification-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Push Notifications</h2>
          <p className="text-muted-foreground">Manage broadcasts, schedules, and templates</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-push-stats">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowTestDialog(true)}
            data-testid="test-notification-btn"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Test User
          </Button>
          <Button 
            size="sm" 
            onClick={() => {
              setBroadcastForm({ title: '', body: '', url: '/loyalty', require_interaction: false });
              setShowBroadcastDialog(true);
            }}
            data-testid="broadcast-btn"
          >
            <Radio className="w-4 h-4 mr-2" />
            Broadcast
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <PushStatsCards stats={stats} />

      {/* Main Content Tabs */}
      <Tabs defaultValue="templates" className="w-full">
        <TabsList className="grid grid-cols-3 sm:grid-cols-6 gap-1 h-auto p-1">
          <TabsTrigger value="templates" className="flex items-center gap-2 px-3 py-2">
            <Bell className="w-4 h-4" />
            <span className="hidden sm:inline">Templates</span>
          </TabsTrigger>
          <TabsTrigger value="scheduled" className="flex items-center gap-2 px-3 py-2">
            <Calendar className="w-4 h-4" />
            <span className="hidden sm:inline">Scheduled</span>
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2 px-3 py-2">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Analytics</span>
          </TabsTrigger>
          <TabsTrigger value="subscribers" className="flex items-center gap-2 px-3 py-2">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Subscribers</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2 px-3 py-2">
            <Clock className="w-4 h-4" />
            <span className="hidden sm:inline">History</span>
          </TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates">
          <TemplatesGrid 
            templates={templates}
            onUseForBroadcast={handleTemplateForBroadcast}
            onUseForScheduled={handleTemplateForScheduled}
          />
        </TabsContent>

        {/* Scheduled Tab */}
        <TabsContent value="scheduled">
          <ScheduledNotificationsList 
            notifications={scheduledNotifications}
            onRefresh={fetchData}
            onEdit={handleEditScheduled}
            onNewSchedule={handleNewScheduled}
          />
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          <PushAnalyticsTab />
        </TabsContent>

        {/* Subscribers Tab */}
        <TabsContent value="subscribers">
          <SubscribersList 
            subscribers={subscribers}
            onTestSubscriber={handleTestSubscriber}
          />
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <BroadcastHistory history={broadcastHistory} />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <BroadcastDialog 
        open={showBroadcastDialog}
        onOpenChange={setShowBroadcastDialog}
        initialForm={broadcastForm}
        onSuccess={fetchData}
      />

      <TestNotificationDialog
        open={showTestDialog}
        onOpenChange={setShowTestDialog}
        initialForm={testForm}
      />

      <ScheduledDialog
        open={showScheduledDialog}
        onOpenChange={(open) => {
          setShowScheduledDialog(open);
          if (!open) {
            setEditingScheduled(null);
            setScheduledForm(null);
          }
        }}
        editingNotification={editingScheduled}
        initialForm={scheduledForm}
        onSuccess={fetchData}
      />
    </div>
  );
}
